/**
 * App shell — the boot sequence that makes the Watchtower Console whole.
 *
 *   loadRuntimeConfig()  →  AuthProvider  →  Gate (Login | NoAccess | Shell)
 *
 * Boot:
 *   1. Fetch /config.json (loadRuntimeConfig, throws a typed ConfigError). The
 *      local-dev fallback (DEV_CONFIG + a visible DEV banner) fires ONLY when the
 *      config is genuinely absent — a 404, or a dev server's SPA history fallback
 *      that returns index.html (a 'parse' failure with text/html). Every other
 *      failure (403/500, a corrupt application/json body, a network error) is a
 *      real deploy malfunction and renders a loud error screen instead of
 *      silently pointing the console at the dev API. In a real deploy the
 *      DashboardStack BucketDeployment writes config.json, so the dev fallback
 *      never fires in production.
 *   2. Wrap the tree in AuthProvider (owns config + token) and, inside it,
 *      QueryProvider (QueryClientProvider + ApiProvider, which reads useAuth).
 *   3. Gate: no token → <Login/>; token but !canRead → <NoAccess/>; else the
 *      cockpit shell (header + hash-routed tab bar + active tab).
 *
 * Routing is hash-based (#runs / #usage), default #runs — no router dependency,
 * just window.location.hash + a hashchange listener. Keeps the static-site
 * deploy trivial (no server rewrites).
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  AuthProvider,
  ConfigError,
  loadRuntimeConfig,
  useAuth,
  type RuntimeConfig,
} from './lib/auth';
import { QueryProvider, makeQueryClient } from './lib/queries';
import Login from './components/Login';
import NoAccess from './components/NoAccess';
import RunsTab from './tabs/RunsTab';
import UsageTab from './tabs/UsageTab';

// ---------------------------------------------------------------------------
// Local-dev fallback config. Only ever used when /config.json is absent (a real
// deploy always ships it via the DashboardStack BucketDeployment). tenantId is
// empty here — Login surfaces a hint to create dashboard/config.local.json with
// a concrete management-tenant id so a local dev can actually sign in.
// ---------------------------------------------------------------------------

const DEV_CONFIG: RuntimeConfig = {
  apiUrl: 'https://api.watchtower.penseur.ai',
  guardiusUrl: 'https://guardius.penseur.ai',
  tenantId: '',
  env: 'dev',
};

// ---------------------------------------------------------------------------
// Tabs.
// ---------------------------------------------------------------------------

const TABS = [
  { id: 'runs', label: 'Runs', Component: RunsTab },
  { id: 'usage', label: 'Usage', Component: UsageTab },
] as const;

type TabId = (typeof TABS)[number]['id'];

const TAB_IDS = TABS.map((t) => t.id) as readonly string[];

/** Read the active tab from the URL hash, defaulting to `runs` for an empty or
 *  unrecognized hash. */
function tabFromHash(): TabId {
  const raw = window.location.hash.replace(/^#/, '');
  return (TAB_IDS.includes(raw) ? raw : 'runs') as TabId;
}

/** Subscribe to hashchange and report the current tab. */
function useHashTab(): TabId {
  const [tab, setTab] = useState<TabId>(tabFromHash);
  useEffect(() => {
    const onHashChange = () => setTab(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  return tab;
}

// ---------------------------------------------------------------------------
// Shell (rendered only when authorized — reads useAuth).
// ---------------------------------------------------------------------------

function Shell() {
  const { config, address, signOut } = useAuth();
  const tab = useHashTab();
  const active = TABS.find((t) => t.id === tab) ?? TABS[0];
  const ActiveTab = active.Component;

  return (
    <div className="app-shell">
      <header className="app-header">
        <span className="app-title">Penseur Watchtower</span>
        <span className="env-pill">{config.env}</span>
        <a
          className="app-version"
          href={`https://github.com/penseurai/penseur-watchtower/commit/${__BUILD_SHA__}`}
          target="_blank"
          rel="noreferrer"
          title="deployed commit"
        >
          {__BUILD_SHA__}
        </a>
        <span className="app-header-spacer" />
        {address && (
          <span className="app-wallet" title={address}>
            {address}
          </span>
        )}
        <button type="button" className="btn app-signout" onClick={signOut}>
          Sign out
        </button>
      </header>

      <nav className="tab-bar" aria-label="Console sections">
        {TABS.map((t) => (
          <a
            key={t.id}
            href={`#${t.id}`}
            className="tab"
            aria-current={t.id === active.id ? 'page' : undefined}
          >
            {t.label}
          </a>
        ))}
      </nav>

      <main className="tab-content">
        <ActiveTab />
      </main>
    </div>
  );
}

/** Auth gate: no token → Login, token but no read permission → NoAccess, else
 *  the Shell. Server-side authorization is authoritative; NoAccess only avoids
 *  rendering a shell the user can't populate. */
function Gate() {
  const { token, canRead, serverDenied } = useAuth();
  if (!token) return <Login />;
  // NoAccess when the client-side gate says no OR the server 403'd a data fetch
  // (spec §5: API 403 → "no access" screen).
  if (!canRead || serverDenied) return <NoAccess />;
  return <Shell />;
}

// ---------------------------------------------------------------------------
// Boot.
// ---------------------------------------------------------------------------

type BootState =
  | { phase: 'loading' }
  | { phase: 'ready'; config: RuntimeConfig; isDev: boolean }
  | { phase: 'error'; message: string };

/** Decide whether a config-load failure is the "config genuinely absent → local
 *  dev" case (→ dev fallback) or a real malfunction (→ loud error screen).
 *
 *  Dev-fallback ONLY for:
 *    - kind 'not-found' (HTTP 404 — no config.json on the origin), or
 *    - kind 'parse' whose response was text/html — a dev server's SPA history
 *      fallback serving index.html for a missing /config.json (200 + text/html +
 *      an <!doctype html> body, so res.json() throws). The ONLY parse case that's
 *      really "absent".
 *
 *  Everything else stays loud: a 403/500 ('http'), a network rejection
 *  ('network'), and crucially a 'parse' on an application/json body — a corrupt
 *  or truncated config.json from a real deploy must NOT masquerade as dev mode. */
function isGenuinelyAbsent(err: unknown): boolean {
  if (!(err instanceof ConfigError)) return false;
  if (err.kind === 'not-found') return true;
  if (err.kind === 'parse') {
    return (err.contentType ?? '').toLowerCase().includes('text/html');
  }
  return false;
}

export default function App() {
  const [boot, setBoot] = useState<BootState>({ phase: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const config = await loadRuntimeConfig();
        if (!cancelled) setBoot({ phase: 'ready', config, isDev: false });
      } catch (err) {
        if (cancelled) return;
        if (isGenuinelyAbsent(err)) {
          // config.json genuinely absent (404 or SPA fallback) → local-dev
          // fallback + DEV banner.
          setBoot({ phase: 'ready', config: DEV_CONFIG, isDev: true });
        } else {
          // A real malfunction (403/500, corrupt JSON, network) — fail loudly
          // rather than silently pointing the console at the dev API.
          const message = err instanceof Error ? err.message : String(err);
          setBoot({ phase: 'error', message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (boot.phase === 'loading') {
    return <div className="boot-fallback">Loading config…</div>;
  }

  if (boot.phase === 'error') {
    return (
      <div className="boot-error" role="alert">
        <strong>Configuration failed to load</strong>
        <p>{boot.message}</p>
      </div>
    );
  }

  return (
    <Providers config={boot.config}>
      {boot.isDev && (
        <div className="dev-banner" role="status">
          DEV — no config.json found; using fallback ({DEV_CONFIG.apiUrl})
        </div>
      )}
      <Gate />
    </Providers>
  );
}

/** Compose the auth + query providers in the required order: AuthProvider on the
 *  outside (owns config + token), QueryProvider inside it (ApiProvider reads
 *  useAuth). The QueryClient is created once and held for the app's lifetime. */
function Providers({ config, children }: { config: RuntimeConfig; children: ReactNode }) {
  const [client] = useState(makeQueryClient);
  return (
    <AuthProvider config={config}>
      <QueryProvider client={client}>{children}</QueryProvider>
    </AuthProvider>
  );
}
